import React, {
  createContext, useContext, useState, useEffect, ReactNode, useRef,
} from 'react'

export type OracleTheme = 'light' | 'dark'

interface OracleThemeContextType {
  theme: OracleTheme
  toggleTheme: () => void
  setTheme: (theme: OracleTheme) => void
  containerRef: React.RefObject<HTMLDivElement>
}

const OracleThemeContext = createContext<OracleThemeContextType | undefined>(undefined)

const STORAGE_KEY = 'oracle-theme'

const getSystemTheme = (): OracleTheme => {
  // If we want to support user-specified themes, you'll need this code:
  // if (typeof window !== 'undefined' && window.matchMedia) {
  //   return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
  // }
  // return 'light'

  // Dark mode is disabled for now, so we default to light mode no matter what.
  return 'light'
}

const getStoredTheme = (): OracleTheme => {
  if (typeof window !== 'undefined') {
    const stored = localStorage.getItem(STORAGE_KEY)
    if (stored === 'light' || stored === 'dark') {
      return stored
    }
  }
  return getSystemTheme()
}

interface OracleThemeProviderProps {
  children: ReactNode
  /** If set, forces the theme to this value and ignores localStorage */
  forcedTheme?: OracleTheme
  /** Additional CSS class for the container (e.g., 'oracle-sos-container') */
  containerClass?: string
}

/**
 * OracleThemeProvider provides a chat-ui-specific theme context and
 * creates a themed container div that applies the theme to its children.
 */
export const OracleThemeProvider: React.FC<OracleThemeProviderProps> = ({
  children,
  forcedTheme,
  containerClass,
}) => {
  const [theme, setThemeState] = useState<OracleTheme>(
    () => forcedTheme ?? getStoredTheme(),
  )
  const containerRef = useRef<HTMLDivElement>(null)

  // When forcedTheme changes, update internal state
  useEffect(() => {
    if (forcedTheme) {
      setThemeState(forcedTheme)
    }
  }, [forcedTheme])

  useEffect(() => {
    const applyTheme = (newTheme: OracleTheme) => {
      if (containerRef.current) {
        if (newTheme === 'dark') {
          containerRef.current.setAttribute('data-oracle-theme', 'dark')
        } else {
          containerRef.current.removeAttribute('data-oracle-theme')
        }
      }
    }

    applyTheme(theme)
    // Only persist to localStorage if not using forced theme
    if (!forcedTheme) {
      localStorage.setItem(STORAGE_KEY, theme)
    }
  }, [theme, forcedTheme])

  const setTheme = (newTheme: OracleTheme) => {
    // No-op if forcedTheme is set
    if (!forcedTheme) {
      setThemeState(newTheme)
    }
  }

  const toggleTheme = () => {
    // No-op if forcedTheme is set
    if (!forcedTheme) {
      setTheme(theme === 'light' ? 'dark' : 'light')
    }
  }

  const className = containerClass
    ? `oracle-theme-container ${containerClass}`
    : 'oracle-theme-container'

  return (
    <div ref={containerRef} className={className}>
      <OracleThemeContext.Provider value={{
        theme, toggleTheme, setTheme, containerRef,
      }}
      >
        {children}
      </OracleThemeContext.Provider>
    </div>
  )
}

export const useOracleTheme = (): OracleThemeContextType => {
  const context = useContext(OracleThemeContext)
  if (context === undefined) {
    throw new Error('useOracleTheme must be used within an OracleThemeProvider')
  }
  return context
}
